import axios from 'axios';
import { generateToken, removeToken } from './token';

const CLIENT_IP_CACHE_KEY = 'client_ip_address';
let ipFetchPromise: Promise<string | null> | null = null;

const getSessionStorageItem = (key: string): string | null => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage
      ? sessionStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
};

const setSessionStorageItem = (key: string, value: string): void => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(key, value);
    }
  } catch {
    return;
  }
};

const fetchClientIpWithTimeout = (timeoutMs = 1500): Promise<string | null> => {
  const fetchPromise = async (): Promise<string | null> => {
    try {
      const cachedIp = getSessionStorageItem(CLIENT_IP_CACHE_KEY);
      if (cachedIp) return cachedIp;

      const response = await fetch('https://api64.ipify.org?format=json');
      if (!response.ok) throw new Error('Falha ao obter IP');

      const data = await response.json();
      if (data && data.ip) {
        setSessionStorageItem(CLIENT_IP_CACHE_KEY, data.ip);
        return data.ip;
      }
      return null;
    } catch {
      try {
        const fallbackResponse = await fetch(
          'https://api.ipify.org?format=json',
        );
        if (!fallbackResponse.ok) return null;
        const fallbackData = await fallbackResponse.json();
        if (fallbackData && fallbackData.ip) {
          setSessionStorageItem(CLIENT_IP_CACHE_KEY, fallbackData.ip);
          return fallbackData.ip;
        }
      } catch {
        return null;
      }
      return null;
    }
  };

  const timeoutPromise = new Promise<null>(resolve => {
    setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([fetchPromise(), timeoutPromise]);
};

const getClientIp = (): Promise<string | null> => {
  const cachedIp = getSessionStorageItem(CLIENT_IP_CACHE_KEY);
  if (cachedIp) return Promise.resolve(cachedIp);

  if (!ipFetchPromise) {
    ipFetchPromise = fetchClientIpWithTimeout().finally(() => {
      ipFetchPromise = null;
    });
  }
  return ipFetchPromise;
};

const createApiClient = (baseURL: string) => {
  const apiInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  apiInstance.interceptors.request.use(
    async config => {
      const accessToken = await generateToken();
      if (accessToken && config?.headers) {
        config.headers.Authorization = `Bearer ${accessToken || ''}`;
      }

      try {
        const clientIp = await getClientIp();
        if (clientIp && config?.headers) {
          config.headers['X-Forwarded-For'] = clientIp;
        }
      } catch (err) {
        console.error('Erro ao adicionar X-Forwarded-For:', err);
      }

      return config;
    },
    error => Promise.reject(error),
  );

  apiInstance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        removeToken(
          'Token expirado, você será redirecionado para realizar login novamente.',
        );
      }
      return Promise.reject(error);
    },
  );

  return apiInstance;
};

export default createApiClient;
